import type {
  AssetPathResponse,
  AssetResponse,
  FormCategoryCountResponse,
  FormDefinitionCategoryCountsResponse,
  FormDefinitionResponse,
  FormDefinitionSimpleResponse,
  FormRequest,
  FormSimpleResponse,
  GetAssetTreeRequest,
  GetFormDefinitionListRequest,
  GetFormListRequestV2,
  GetListResponseOfFormDefinitionSimpleResponse,
  GetListResponseOfFormSimpleResponse,
  GetListResponseOfSelectOption,
  GetPersonnelSelectOptionsRequest,
  OfflineAssetRow,
  OfflineAssetSection,
  OfflinePersonnelSection,
  PersonnelResponse,
  PersonnelSimpleResponse,
  TreeResponse,
} from 'src/api/ProactApiClient';
import {
  FormStateEnum,
  UserStatusEnum,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import {isEffectivelyOffline} from 'src/offline/connectivity';
import {offlineDb} from 'src/offline/db';
import {offlineQuery, readSnapshotData} from 'src/offline/offlineQuery';
import {getReadySession} from 'src/offline/offlineSessionManager';
import {PENDING_STATUSES} from 'src/offline/outbox';
import {
  FORMS_PERSONNEL_SOURCE,
  type PersonnelSectionSource,
} from 'src/offline/personnelSources';
import {inProjectScope, projectScopeId} from 'src/offline/projectScope';
import type {OutboxItem} from 'src/offline/types';
import {clientQuery} from 'src/redux/clientQuery';
import type {ClientQueryResult} from 'src/redux/clientQuery';
import {store} from 'src/redux/store';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import {buildTree} from 'src/shared/utils/treeUtils';
import {assetTreeKinds} from 'src/app/assets/assetTreeBrowserState';
import type {IStateMachine} from 'src/types/StateMachine';
import {FORM_OUTBOX_KINDS} from './localFiles';
import {
  formDefinitionKey,
  formDefinitionsListKey,
  formsAssetsKey,
  formStateMachineKey,
  FORMS_CONTEXT_KEY,
} from './snapshotKeys';

/** The URL the fill Save bar passes to the state-machine query. */
export const FORM_STATE_MACHINE_URL = `${ApiEndpoints.getStaticForm}/state-machine`;

// The forms session is always active online, so reads only divert to it while
// offline — online users keep getting fresh definitions from the network.
const formsContext = {
  contextKey: () => FORMS_CONTEXT_KEY,
  shouldDivert: () => isEffectivelyOffline(),
};

const matchesSearch = (
  definition: FormDefinitionResponse,
  search: string | undefined
): boolean => {
  if (!search) return true;
  const term = search.toLowerCase();
  return (
    definition.name.toLowerCase().includes(term) ||
    definition.title.toLowerCase().includes(term) ||
    (definition.uniqueIdentifier?.toLowerCase().includes(term) ?? false)
  );
};

// The offline bundle already excludes subform-only and extension-only
// definitions; this defensively mirrors the list request's include flags.
const isListable = (
  definition: FormDefinitionResponse,
  params: GetFormDefinitionListRequest
): boolean =>
  (params.includeSubforms === true || definition.subformOnly !== true) &&
  (params.includeExtensionOnly === true || definition.extensionOnly !== true);

const readListableDefinitions = async (
  sessionId: string,
  params: GetFormDefinitionListRequest
): Promise<FormDefinitionResponse[] | undefined> => {
  const definitions = await readSnapshotData<FormDefinitionResponse[]>(
    sessionId,
    formDefinitionsListKey
  );
  if (!definitions) return undefined;
  const scopeId = projectScopeId();
  const scoped =
    scopeId === undefined
      ? definitions
      : definitions.filter(definition =>
        inProjectScope(definition.projectId, scopeId)
      );
  return scoped.filter(definition => isListable(definition, params));
};

const byName = (a: FormDefinitionResponse, b: FormDefinitionResponse) =>
  a.name.localeCompare(b.name, undefined, {sensitivity: 'base'});

// Mirrors server paging: name order, totalRecords counts the filtered set.
export const formDefinitionListQueryFn = (
  call: (
    params: GetFormDefinitionListRequest
  ) => Promise<GetListResponseOfFormDefinitionSimpleResponse>
) =>
  offlineQuery<
    GetFormDefinitionListRequest,
    GetListResponseOfFormDefinitionSimpleResponse
  >({
    ...formsContext,
    read: async (session, params) => {
      const listable = await readListableDefinitions(session.id, params);
      if (!listable) return undefined;
      const filtered = listable
        .filter(
          definition =>
            (params.categoryId === undefined ||
              definition.categoryId === params.categoryId) &&
            matchesSearch(definition, params.search)
        )
        .sort(byName);
      const page =
        params.length > 0
          ? filtered.slice(params.start, params.start + params.length)
          : filtered;
      return {
        filteredRecords: page.length,
        totalRecords: filtered.length,
        // Bundle stores full FormDefinitionResponse — a superset of the
        // simple shape — so the list fields the UI reads are all present.
        data: page as unknown as FormDefinitionSimpleResponse[],
      };
    },
    call,
  });

// My Forms defaults: subform-only and extension-only definitions excluded.
const MY_FORMS_LIST_PARAMS: GetFormDefinitionListRequest = {start: 0, length: 0};

export const formDefinitionCategoryCountsQueryFn = (
  call: () => Promise<FormDefinitionCategoryCountsResponse>
) =>
  offlineQuery<void, FormDefinitionCategoryCountsResponse>({
    ...formsContext,
    read: async session => {
      const listable = await readListableDefinitions(
        session.id,
        MY_FORMS_LIST_PARAMS
      );
      if (!listable) return undefined;
      const active = listable.filter(definition => definition.active);
      const counts = new Map<number, FormCategoryCountResponse>();
      for (const {category} of active) {
        if (!category) continue;
        const entry = counts.get(category.id);
        if (entry) entry.count += 1;
        else counts.set(category.id, {id: category.id, name: category.name, count: 1});
      }
      return {
        total: active.length,
        categories: [...counts.values()].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {sensitivity: 'base'})
        ),
      };
    },
    call,
  });

// Offline serves the cached definition by id; online (or on a miss) hits the
// network. Shared by the plain and flattened reads via their id extraction.
const definitionByIdQueryFn = <Arg>(
  getId: (arg: Arg) => number,
  call: (arg: Arg) => Promise<FormDefinitionResponse>
) =>
  offlineQuery<Arg, FormDefinitionResponse>({
    ...formsContext,
    read: (session, arg) =>
      readSnapshotData(session.id, formDefinitionKey(getId(arg))),
    call,
  });

export const formDefinitionQueryFn = (
  call: (id: number) => Promise<FormDefinitionResponse>
) => definitionByIdQueryFn<number>(id => id, call);

export interface FormDefinitionFlattenedArg {
  id: number;
  flattenSubform: boolean;
}

export const formDefinitionFlattenedQueryFn = (
  call: (arg: FormDefinitionFlattenedArg) => Promise<FormDefinitionResponse>
) => definitionByIdQueryFn<FormDefinitionFlattenedArg>(arg => arg.id, call);

/** A not-yet-synced create, rendered in the submissions table until sync
 * swaps it for the real server row. */
export type PendingFormRow = FormSimpleResponse & {pendingSync: true};

// The signed-in user stands in as the creator; only creator.name is rendered,
// so the remaining PersonnelSimpleResponse fields are left unset.
const pendingCreator = (): PersonnelSimpleResponse => {
  const user = store.getState().session.user;
  return {
    id: user?.id ?? 0,
    name: user?.name ?? 'You',
    email: user?.email ?? '',
  } as unknown as PersonnelSimpleResponse;
};

const toPendingRow = (
  item: OutboxItem,
  creator: PersonnelSimpleResponse,
  definitionsById: Map<number, FormDefinitionResponse>
): PendingFormRow => {
  const payload = item.payload as FormRequest;
  return {
    id: -item.seq,
    shortDescription: payload.shortDescription,
    formDefinitionId: payload.formDefinitionId,
    formDefinition: definitionsById.get(payload.formDefinitionId) as
      | FormDefinitionSimpleResponse
      | undefined,
    state: FormStateEnum.Draft,
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.createdAt),
    creator,
    anonymousSubmission: false,
    pendingSync: true,
  } as PendingFormRow;
};

// Definition ids the active scope allows, or undefined when nothing is scoped.
const scopedDefinitionIds = (
  definitions: FormDefinitionResponse[]
): Set<number> | undefined => {
  const scopeId = projectScopeId();
  if (scopeId === undefined) return undefined;
  return new Set(
    definitions
      .filter(definition => inProjectScope(definition.projectId, scopeId))
      .map(definition => definition.id)
  );
};

// Offline the submissions list is served from the outbox: unsynced creates of
// the forms session become pending rows so the table renders instead of erroring.
export const formListV2QueryFn = (
  call: (
    params: GetFormListRequestV2
  ) => Promise<GetListResponseOfFormSimpleResponse>
) =>
  offlineQuery<GetFormListRequestV2, GetListResponseOfFormSimpleResponse>({
    ...formsContext,
    read: async (session, params) => {
      const items = await offlineDb.outbox
        .where('sessionId')
        .equals(session.id)
        .toArray();
      const creator = pendingCreator();
      const definitions =
        (await readSnapshotData<FormDefinitionResponse[]>(
          session.id,
          formDefinitionsListKey
        )) ?? [];
      const definitionsById = new Map(
        definitions.map(definition => [definition.id, definition])
      );
      const inScope = scopedDefinitionIds(definitions);
      const rows = items
        .filter(
          item =>
            item.kind === FORM_OUTBOX_KINDS.create &&
            PENDING_STATUSES.includes(item.status) &&
            (params.formDefinitionId === undefined ||
              (item.payload as FormRequest).formDefinitionId ===
                params.formDefinitionId) &&
            (inScope === undefined ||
              inScope.has((item.payload as FormRequest).formDefinitionId))
        )
        .map(item => toPendingRow(item, creator, definitionsById));
      return {
        data: rows,
        totalRecords: rows.length,
        filteredRecords: rows.length,
      };
    },
    call,
  });

// Serves the cached forms state machine offline so the fill Save bar renders;
// only the forms URL is cached, every other state-machine URL passes through.
export const formStateMachineQueryFn = (
  call: (url: string) => Promise<IStateMachine>
) =>
  offlineQuery<string, IStateMachine>({
    contextKey: url => (url === FORM_STATE_MACHINE_URL ? FORMS_CONTEXT_KEY : undefined),
    shouldDivert: formsContext.shouldDivert,
    read: session => readSnapshotData(session.id, formStateMachineKey),
    call,
  });

// A section serves only under the project scope it was captured in; personnel
// access is many-to-many, so rows cannot be re-filtered client-side.
const readSection = async <T extends {scopeProjectId?: number}>(
  sessionId: string,
  key: string
): Promise<T | undefined> => {
  const section = await readSnapshotData<T>(sessionId, key);
  if (!section) return undefined;
  return (section.scopeProjectId ?? undefined) === projectScopeId()
    ? section
    : undefined;
};

const findAssetRow = async (
  sessionId: string,
  id: number
): Promise<OfflineAssetRow | undefined> => {
  const section = await readSection<OfflineAssetSection>(
    sessionId,
    formsAssetsKey
  );
  return section?.rows.find(row => row.id === id);
};

const BUNDLED_USER_TYPES = [UserTypeEnum.Contractor, UserTypeEnum.Employee];

// The bundle covers exactly the PersonSelectBox request shape; anything else
// (supervisor-only, other user types, unbundled states) misses to the network.
const isBundledSelectRequest = (
  params: GetPersonnelSelectOptionsRequest,
  section: OfflinePersonnelSection
): boolean => {
  const states = section.includesInactive
    ? [UserStatusEnum.Active, UserStatusEnum.Inactive]
    : [UserStatusEnum.Active];
  return (
    params.superviserOnly !== true &&
    (params.userTypes?.length ?? 0) > 0 &&
    params.userTypes!.every(type => BUNDLED_USER_TYPES.includes(type)) &&
    (params.active ?? []).every(state => states.includes(state))
  );
};

// The first ready session holding an in-scope section wins; every offline
// domain that bundles personnel can serve the same select box.
const readPersonnelSection = async (
  sources: PersonnelSectionSource[]
): Promise<OfflinePersonnelSection | undefined> => {
  for (const source of sources) {
    const session = await getReadySession(source.contextKey);
    if (!session) continue;
    const section = await readSection<OfflinePersonnelSection>(
      session.id,
      source.snapshotKey
    );
    if (section) return section;
  }
  return undefined;
};

// Offline search covers the name only — job code, email and employee id are
// not bundled, a size trade-off.
export const personnelSelectOptionsQueryFn =
  (
    call: (
      params: GetPersonnelSelectOptionsRequest
    ) => Promise<GetListResponseOfSelectOption>,
    sources: PersonnelSectionSource[] = [FORMS_PERSONNEL_SOURCE]
  ) =>
  async (
    params: GetPersonnelSelectOptionsRequest
  ): Promise<ClientQueryResult<GetListResponseOfSelectOption>> => {
    if (isEffectivelyOffline()) {
      const section = await readPersonnelSection(sources);
      if (section && isBundledSelectRequest(params, section)) {
        const userTypes = params.userTypes ?? [];
        const active = params.active ?? [];
        const scoped = section.rows.filter(
          row =>
            userTypes.includes(row.userType) &&
            (active.length === 0 || active.includes(row.active))
        );
        const term = params.search?.toLowerCase();
        const filtered = term
          ? scoped.filter(row => row.name.toLowerCase().includes(term))
          : scoped;
        const page = filtered.slice(params.start, params.start + params.length);
        return {
          data: {
            totalRecords: scoped.length,
            filteredRecords: filtered.length,
            data: page.map(row => ({id: row.id, label: row.name})),
          },
        };
      }
    }
    return clientQuery(call)(params);
  };

// The bundled row carries no supervisor/department, so the view hides the
// description line offline (see FormPersonSelectorV2).
export const personQueryFn =
  (
    call: (id: number) => Promise<PersonnelResponse>,
    sources: PersonnelSectionSource[] = [FORMS_PERSONNEL_SOURCE]
  ) =>
  async (id: number): Promise<ClientQueryResult<PersonnelResponse>> => {
    if (isEffectivelyOffline()) {
      const section = await readPersonnelSection(sources);
      const row = section?.rows.find(candidate => candidate.id === id);
      if (row) {
        return {
          data: {
            id: row.id,
            name: row.name,
            active: row.active,
            userType: row.userType,
          } as unknown as PersonnelResponse,
        };
      }
    }
    return clientQuery(call)(id);
  };

export const assetTreeQueryFn = (
  call: (params: GetAssetTreeRequest) => Promise<TreeResponse[]>
) =>
  offlineQuery<GetAssetTreeRequest, TreeResponse[]>({
    ...formsContext,
    read: async (session, params) => {
      const states = params.activeStates ?? [];
      if (states.length !== 1 || states[0] !== true) return undefined;
      const section = await readSection<OfflineAssetSection>(
        session.id,
        formsAssetsKey
      );
      if (!section) return undefined;
      const rows =
        params.isLeaf === undefined
          ? section.rows
          : section.rows.filter(row => row.isLeaf === params.isLeaf);
      return buildTree(
        rows.map(row => ({
          ...row,
          kind: row.isLeaf ? assetTreeKinds.asset : assetTreeKinds.group,
        })),
        params.parentId
      );
    },
    call,
  });

export const assetQueryFn = (call: (id: number) => Promise<AssetResponse>) =>
  offlineQuery<number, AssetResponse>({
    ...formsContext,
    read: async (session, id) => {
      const row = await findAssetRow(session.id, id);
      if (!row) return undefined;
      return {
        id: row.id,
        name: row.name,
        isLeaf: row.isLeaf,
        active: true,
        parentId: row.parentId,
        path: row.path,
      } as unknown as AssetResponse;
    },
    call,
  });

export const assetPathQueryFn = (
  call: (id: number) => Promise<AssetPathResponse>
) =>
  offlineQuery<number, AssetPathResponse>({
    ...formsContext,
    read: async (session, id) => {
      const row = await findAssetRow(session.id, id);
      return row ? {id: row.id, name: row.name, path: row.path} : undefined;
    },
    call,
  });

// Offline the task detail page reads definition names for field meta only:
// serve the cached catalog, and an empty list rather than an error on a miss.
export const formDefinitionSelectOptionsQueryFn = (
  call: (
    params: GetFormDefinitionListRequest
  ) => Promise<GetListResponseOfSelectOption>
) =>
  offlineQuery<GetFormDefinitionListRequest, GetListResponseOfSelectOption>({
    ...formsContext,
    read: async session => {
      const definitions =
        (await readSnapshotData<FormDefinitionResponse[]>(
          session.id,
          formDefinitionsListKey
        )) ?? [];
      const data = definitions.map(definition => ({
        id: definition.id,
        label: definition.title || definition.name,
      }));
      return {
        data,
        totalRecords: data.length,
        filteredRecords: data.length,
      };
    },
    call,
  });
